import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";
import { ContactModal } from "./ContactModal";
import { ImportContactsModal } from "./ImportContactsModal";
import { ImportDomainsModal } from "./ImportDomainsModal";
import ValidationPopup from "../../../History/Modals/ValidationPopup";
import Alert from "../../../../Controls/Alerts/Alerts";

export function ContactGroupModal({ isOpen, onClose, onSave, initialGroup, contacts = [], availableContacts = [], contactGroups = [], suggestedDomains = [], onDomainsImport, onContactsImport }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const contactList = availableContacts.length > 0 ? availableContacts : contacts;

  // Active sub-tab: 'domains' | 'contacts'
  const [activeTab, setActiveTab] = useState("domains");

  // Transfer list sub-filters: 'inuse' | 'suggested'
  const [domainFilter, setDomainFilter] = useState("inuse");
  const [contactFilter, setContactFilter] = useState("inuse");

  // Form Data
  const [name, setName] = useState("");
  const [aliasEmail, setAliasEmail] = useState("");
  const [description, setDescription] = useState("");
  const [isDistributionList, setIsDistributionList] = useState(false);
  const [autoAddUsers, setAutoAddUsers] = useState(false);
  const [autoAddDomains, setAutoAddDomains] = useState(false);

  // Transfer list selected states
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  // Local additions from inner modals
  const [newlyImportedContacts, setNewlyImportedContacts] = useState([]);

  // Domain entry & search queries
  const [domainInput, setDomainInput] = useState("");
  const [searchQueryAvailableDomains, setSearchQueryAvailableDomains] = useState("");
  const [searchQuerySelectedDomains, setSearchQuerySelectedDomains] = useState("");
  const [searchQueryAvailable, setSearchQueryAvailable] = useState("");
  const [searchQuerySelected, setSearchQuerySelected] = useState("");

  // Sub-modals & confirmation dialogs
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportDomainsModalOpen, setIsImportDomainsModalOpen] = useState(false);
  const [isValidationPopup, setIsValidationPopup] = useState(false);
  const [validationData, setValidationData] = useState({});
  const [pendingDomainsToAdd, setPendingDomainsToAdd] = useState(null);
  const [pendingContactsToAdd, setPendingContactsToAdd] = useState(null);

  // Alert toaster state
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("");

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setAlertMsg("");
    setAlertType("");
    if (initialGroup) {
      setName(initialGroup.name || "");
      setAliasEmail(initialGroup.aliasEmail || "");
      setDescription(initialGroup.description || "");
      setIsDistributionList(initialGroup.isDistributionList || false);
      setAutoAddUsers(initialGroup.autoAddUsers || false);
      setSelectedDomains(initialGroup.domains || []);
      const rawMembers = initialGroup.memberContactIds || initialGroup.contactIds || [];
      const cleanMemberEmails = rawMembers.map((x) => {
        const strVal = String(x).trim();
        const found = (contactList || []).find((c) => c && ((c.id && c.id === strVal) || (c.Id && c.Id === strVal) || (c._id && c._id === strVal) || (c.email && c.email.toLowerCase() === strVal.toLowerCase())));
        if (found && found.email) {
          return found.email.toLowerCase().trim();
        }
        return strVal.toLowerCase();
      }).filter((x) => x && !x.startsWith("contact-"));
      setSelectedContactIds(cleanMemberEmails);
    } else {
      setName("");
      setAliasEmail("");
      setDescription("");
      setIsDistributionList(false);
      setAutoAddUsers(false);
      setSelectedDomains([]);
      setSelectedContactIds([]);
    }
    setActiveTab("domains");
    setDomainFilter("inuse");
    setContactFilter("inuse");
    setAutoAddDomains(false);
    setNewlyImportedContacts([]);
    setDomainInput("");
    setSearchQueryAvailableDomains("");
    setSearchQuerySelectedDomains("");
    setSearchQueryAvailable("");
    setSearchQuerySelected("");
    setPendingDomainsToAdd(null);
    setPendingContactsToAdd(null);
    setErrors({});
  }, [initialGroup, isOpen]);

  // Combined contacts pool (deduplicated strictly by lowercased email address)
  const allCombinedContacts = useMemo(() => {
    const map = new Map();
    newlyImportedContacts.forEach((c) => {
      const emailKey = (c.email || "").toLowerCase().trim();
      if (emailKey && !emailKey.startsWith("contact-")) {
        map.set(emailKey, { ...c, email: emailKey });
      }
    });
    contactList.forEach((c) => {
      const emailKey = (c.email || "").toLowerCase().trim();
      if (emailKey && !emailKey.startsWith("contact-")) {
        if (!map.has(emailKey)) {
          map.set(emailKey, { ...c, email: emailKey });
        } else {
          const localC = map.get(emailKey);
          map.set(emailKey, { ...c, ...localC, email: emailKey });
        }
      }
    });
    if (initialGroup) {
      const members = initialGroup.memberContacts || initialGroup.members || [];
      members.forEach((c) => {
        if (typeof c === "object" && c.email) {
          const emailKey = c.email.toLowerCase().trim();
          if (emailKey && !emailKey.startsWith("contact-") && !map.has(emailKey)) {
            map.set(emailKey, { ...c, email: emailKey });
          }
        }
      });
      const memberIds = initialGroup.memberContactIds || initialGroup.contactIds || [];
      memberIds.forEach((idOrEmail) => {
        if (idOrEmail) {
          const strVal = String(idOrEmail).trim();
          const found = (contactList || []).find((c) => c && ((c.id && c.id === strVal) || (c.Id && c.Id === strVal) || (c._id && c._id === strVal) || (c.email && c.email.toLowerCase() === strVal.toLowerCase())));
          if (found && found.email) {
            const emailKey = found.email.toLowerCase().trim();
            if (!map.has(emailKey)) {
              map.set(emailKey, { ...found, email: emailKey });
            }
          } else {
            const emailKey = strVal.toLowerCase();
            if (emailKey && !emailKey.startsWith("contact-") && !map.has(emailKey)) {
              map.set(emailKey, {
                id: emailKey,
                email: emailKey,
                firstName: "",
                lastName: "",
                isGroupOnly: true
              });
            }
          }
        }
      });
    }
    return Array.from(map.values());
  }, [contactList, newlyImportedContacts, initialGroup]);

  // Auto-add domains effect when autoAddDomains is active
  useEffect(() => {
    if (autoAddDomains) {
      const selectedSet = new Set(selectedContactIds.map((id) => String(id).toLowerCase().trim()));
      const domainsFromSelected = new Set();
      allCombinedContacts.forEach((c) => {
        const emailKey = (c.email || "").toLowerCase().trim();
        if (emailKey && selectedSet.has(emailKey)) {
          if (c.email && c.email.includes("@")) {
            const domain = c.email.split("@")[1]?.toLowerCase().trim();
            if (domain && domain.includes(".")) {
              domainsFromSelected.add(domain);
            }
          }
        }
      });
      if (domainsFromSelected.size > 0) {
        setSelectedDomains((prev) => Array.from(new Set([...prev, ...Array.from(domainsFromSelected)])));
        setAutoAddUsers(true);
      }
    }
  }, [autoAddDomains, allCombinedContacts, selectedContactIds]);

  // Derived all suggested domains pool
  const allAvailableDomains = useMemo(() => {
    const domains = new Set();
    allCombinedContacts.forEach((c) => {
      if (!c.isHiddenFromDirectory && !c.isGroupOnly && c.email && c.email.includes("@")) {
        const d = c.email.split("@")[1]?.toLowerCase().trim();
        if (d && d.includes(".")) domains.add(d);
      }
    });
    suggestedDomains.forEach((d) => { if (d) domains.add(d.toLowerCase().trim()); });
    return Array.from(domains).sort();
  }, [allCombinedContacts, suggestedDomains]);

  // Derived in-use domains across all created groups
  const inUseDomains = useMemo(() => {
    const set = new Set(selectedDomains.map((d) => d.toLowerCase()));
    const validSuggestedSet = new Set(suggestedDomains.map((d) => d.toLowerCase()));

    contactGroups.forEach((g) => {
      if (Array.isArray(g.domains)) {
        g.domains.forEach((d) => {
          if (d) {
            const cleanD = d.toLowerCase().trim();
            if (validSuggestedSet.has(cleanD)) {
              set.add(cleanD);
            }
          }
        });
      }
    });
    return Array.from(set).sort();
  }, [selectedDomains, contactGroups, suggestedDomains]);

  // Derived in-use contacts across all created groups
  const inUseContacts = useMemo(() => {
    const set = new Set(selectedContactIds.map((id) => String(id).toLowerCase().trim()));
    contactGroups.forEach((g) => {
      const memberIds = g.memberContactIds || g.contactIds || [];
      if (Array.isArray(memberIds)) {
        memberIds.forEach((id) => {
          const clean = String(id).toLowerCase().trim();
          if (clean && !clean.startsWith("contact-")) set.add(clean);
        });
      }
    });
    return allCombinedContacts.filter((c) => {
      const emailKey = (c.email || "").toLowerCase().trim();
      return emailKey && set.has(emailKey);
    });
  }, [selectedContactIds, contactGroups, allCombinedContacts]);

  // Filtered left panel domains
  const filteredLeftDomains = useMemo(() => {
    const query = searchQueryAvailableDomains.toLowerCase().trim();
    const sourceList = domainFilter === "inuse" ? inUseDomains : allAvailableDomains;
    if (!query) return sourceList;
    return sourceList.filter((d) => d.toLowerCase().includes(query));
  }, [domainFilter, inUseDomains, allAvailableDomains, searchQueryAvailableDomains]);

  // Filtered right panel allowed domains
  const filteredSelectedDomains = useMemo(() => {
    const query = searchQuerySelectedDomains.toLowerCase().trim();
    if (!query) return selectedDomains;
    return selectedDomains.filter((d) => d.toLowerCase().includes(query));
  }, [selectedDomains, searchQuerySelectedDomains]);

  // Selected contacts list for right panel (Group Members)
  const selectedContactsList = useMemo(() => {
    const selectedSet = new Set(selectedContactIds.map((id) => String(id).toLowerCase().trim()));
    return allCombinedContacts.filter((c) => {
      const emailKey = (c.email || "").toLowerCase().trim();
      return emailKey && selectedSet.has(emailKey);
    });
  }, [allCombinedContacts, selectedContactIds]);

  // Filtered left panel contacts based on contactFilter ('inuse' | 'suggested') & search query
  const filteredLeftContacts = useMemo(() => {
    const q = searchQueryAvailable.toLowerCase().trim();
    const sourceList = contactFilter === "inuse" ? inUseContacts : allCombinedContacts.filter((c) => !c.isHiddenFromDirectory && !c.isGroupOnly);
    return sourceList.filter((c) => {
      const nameStr = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      const emailStr = (c.email || "").toLowerCase();
      return !q || nameStr.includes(q) || emailStr.includes(q);
    });
  }, [contactFilter, inUseContacts, allCombinedContacts, searchQueryAvailable]);

  // Filtered right panel members
  const filteredSelectedContacts = useMemo(() => {
    const q = searchQuerySelected.toLowerCase().trim();
    if (!q) return selectedContactsList;
    return selectedContactsList.filter((c) => {
      const nameStr = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
      const emailStr = (c.email || "").toLowerCase();
      return nameStr.includes(q) || emailStr.includes(q);
    });
  }, [selectedContactsList, searchQuerySelected]);

  if (!isOpen) return null;

  // Domain Management Handlers
  const handleAddDomain = (e) => {
    if (e.type === "keydown" && e.key !== "Enter") return;
    if (e.type === "keydown") e.preventDefault();
    const raw = domainInput.trim().toLowerCase().replace(/^@/, "");
    if (!raw) return;
    const domainRegex = /^[a-z0-9.-]+\.[a-z]{2,}$/;
    if (!domainRegex.test(raw)) {
      setAlertMsg(selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidDomainName);
      setAlertType("warning");
      return;
    }
    if (selectedDomains.map((d) => d.toLowerCase()).includes(raw)) {
      setDomainInput("");
      return;
    }
    const isNew = !suggestedDomains.map((d) => d.toLowerCase()).includes(raw);
    if (isNew) {
      setPendingDomainsToAdd([raw]);
    } else {
      setSelectedDomains((prev) => [...prev, raw]);
    }
    setDomainInput("");
  };

  const handleImportDomainsFromModal = (validDomains, duplicateFound = false) => {
    if (!validDomains || validDomains.length === 0) return;
    const currentSelectedSet = new Set(selectedDomains.map((d) => d.toLowerCase()));
    let fileDuplicateFound = duplicateFound;
    const uniqueNewValidDomains = Array.from(new Set(validDomains)).filter((d) => {
      if (currentSelectedSet.has(d)) {
        fileDuplicateFound = true;
        return false;
      }
      return true;
    });

    if (uniqueNewValidDomains.length > 0) {
      const suggestedSet = new Set((suggestedDomains || []).map((d) => d.toLowerCase()));
      const alreadySuggested = [];
      const newToAsk = [];
      uniqueNewValidDomains.forEach((d) => {
        if (suggestedSet.has(d)) {
          alreadySuggested.push(d);
        } else {
          newToAsk.push(d);
        }
      });
      if (alreadySuggested.length > 0) {
        setSelectedDomains((prev) => Array.from(new Set([...prev, ...alreadySuggested])));
      }
      if (newToAsk.length > 0) {
        setPendingDomainsToAdd(newToAsk);
      }
    }

    if (fileDuplicateFound) {
      setAlertMsg(selectedLanguage?.LstLanguageCodes?.UI_DuplicateEmailAddressesWereRemoved);
      setAlertType("warning");
    }
  };

  const handleSelectDomainFromLeft = (domain) => {
    if (!selectedDomains.includes(domain)) {
      setSelectedDomains((prev) => [...prev, domain]);
    }
  };

  const handleRemoveDomainFromRight = (domain) => { setSelectedDomains((prev) => prev.filter((d) => d !== domain)); };

  const handleSelectContactFromLeft = (contact) => {
    const rawEmail = typeof contact === "object" ? (contact.email || contact.id) : contact;
    if (!rawEmail) return;
    const cleanEmail = String(rawEmail).toLowerCase().trim();
    if (cleanEmail.startsWith("contact-")) return;
    setSelectedContactIds((prev) => Array.from(new Set([...prev, cleanEmail])));
  };

  const handleRemoveContactFromRight = (contact) => {
    const rawEmail = typeof contact === "object" ? (contact.email || contact.id) : contact;
    if (!rawEmail) return;
    const cleanEmail = String(rawEmail).toLowerCase().trim();
    setSelectedContactIds((prev) => prev.filter((id) => String(id).toLowerCase().trim() !== cleanEmail));
  };

  const handleSaveContactFromModal = (newContact) => {
    if (!newContact || !newContact.email) return;
    const cleanContact = {
      ...newContact,
      email: newContact.email.trim().toLowerCase()
    };
    setPendingContactsToAdd([cleanContact]);
    setIsContactModalOpen(false);
  };

  const handleImportContactsFromModal = (importedList, duplicateFound = false) => {
    const existingEmailsMap = new Map(allCombinedContacts.map((c) => [c.email.toLowerCase(), c.email.toLowerCase()]));
    const emailsToAdd = [];
    const importedToKeep = [];
    importedList.forEach((nc) => {
      if (!nc.email) return;
      const email = nc.email.toLowerCase().trim();
      if (existingEmailsMap.has(email)) {
        emailsToAdd.push(email);
      } else {
        importedToKeep.push({ ...nc, email });
      }
    });
    if (emailsToAdd.length > 0) {
      setSelectedContactIds((prev) => Array.from(new Set([...prev, ...emailsToAdd])));
    }
    if (importedToKeep.length > 0) {
      setPendingContactsToAdd(importedToKeep);
    }
    if (duplicateFound) {
      setAlertMsg(selectedLanguage?.LstLanguageCodes?.UI_DeDuplicateMsg);
      setAlertType("warning");
    }
    setIsImportModalOpen(false);
  };

  const validate = () => {
    const newErrors = {};
    const trimmedName = name.trim();
    const trimmedAlias = aliasEmail.trim();

    if (!trimmedName) {
      newErrors.name = selectedLanguage?.LstLanguageCodes?.UI_GroupNameIsRequired;
    } else {
      const isDuplicateName = (contactGroups || []).some((g) => {
        if (initialGroup && (g.id === initialGroup.id || g.name?.toLowerCase() === initialGroup.name?.toLowerCase())) {
          return false;
        }
        return (g.name || "").trim().toLowerCase() === trimmedName.toLowerCase();
      });
      if (isDuplicateName) {
        newErrors.name = selectedLanguage?.LstLanguageCodes?.UI_GroupNameAlreadyExists;
      }
    }

    if (!trimmedAlias) {
      newErrors.aliasEmail = selectedLanguage?.LstLanguageCodes?.UI_GroupEmailIsRequired;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedAlias)) {
        newErrors.aliasEmail = selectedLanguage?.LstLanguageCodes?.UI_Pleaseentervalidmail;
      } else {
        const isDuplicateEmail = (contactGroups || []).some((g) => {
          if (initialGroup && (g.id === initialGroup.id || g.aliasEmail?.toLowerCase() === initialGroup.aliasEmail?.toLowerCase())) {
            return false;
          }
          return (g.aliasEmail || g.email || "").trim().toLowerCase() === trimmedAlias.toLowerCase();
        });
        if (isDuplicateEmail) {
          newErrors.aliasEmail = selectedLanguage?.LstLanguageCodes?.UI_GroupEmailAlreadyExists;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const resolvedMemberIds = Array.from(new Set(selectedContactsList.map((c) => (c.email || "").toLowerCase().trim()).filter(Boolean)));
    const groupData = {
      ...(initialGroup ? { id: initialGroup.id } : {}),
      name: name.trim(),
      aliasEmail: aliasEmail.trim().toLowerCase(),
      description: description.trim(),
      isDistributionList,
      autoAddUsers,
      domains: selectedDomains,
      memberContactIds: resolvedMemberIds,
      contactIds: resolvedMemberIds,
      memberContacts: selectedContactsList,
      members: selectedContactsList,
      scope: initialGroup?.scope || undefined
    };
    onSave(groupData);
  };

  return (
    <div className="modalCus contacts-modal-show contacts-modal-wrapper">
      {alertMsg && (
        <Alert alertType={alertType} alertMsg={alertMsg} setAlertMsg={setAlertMsg} setAlertType={setAlertType} />
      )}
      <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-group">
        <div className="modal-content border-r4">
          {/* Header */}
          <div className="modal-header d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-danger fw-bold font-20">
              {initialGroup ? selectedLanguage?.LstLanguageCodes?.UI_EditContactGroup : selectedLanguage?.LstLanguageCodes?.UI_CreateContactGroup}
            </h4>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0" onClick={onClose}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {/* Top Form Fields */}
            <div className="row g-3 mb-3">
              <div className="col-md-6 readerEdit-mail">
                <label className="col-form-label font-w600 control py-0 mb-1 d-block contacts-form-label">
                  {selectedLanguage?.LstLanguageCodes?.UI_GroupName}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input type="text" placeholder="e.g. Marketing Team" value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name)
                      setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  className={`clsFnLn w-100 form-control ${errors.name ? "border-danger is-invalid" : ""}`} autoFocus
                />
                {errors.name && (<div className="text-danger font-12 mt-1">{errors.name}</div>)}
              </div>

              <div className="col-md-6 readerEdit-mail">
                <label className="col-form-label font-w600 control py-0 mb-1 d-block contacts-form-label">
                  {selectedLanguage?.LstLanguageCodes?.UI_GroupEmailDistributionList}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input disabled={initialGroup} type="email" placeholder="e.g. marketing@example.com" value={aliasEmail}
                  onChange={(e) => {
                    setAliasEmail(e.target.value);
                    if (errors.aliasEmail)
                      setErrors((prev) => ({ ...prev, aliasEmail: undefined }));
                  }}
                  className={`clsFnLn w-100 form-control ${errors.aliasEmail ? "border-danger is-invalid" : ""}`}
                />
                {errors.aliasEmail && (<div className="text-danger font-12 mt-1">{errors.aliasEmail}</div>)}
              </div>

              <div className="col-12 readerEdit-mail">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="col-form-label font-w600 control py-0 mb-0 d-block contacts-form-label">
                    {selectedLanguage?.LstLanguageCodes?.UI_DescriptionOptional}
                  </label>
                  <span className={`font-11 ${description.length >= 264 ? "text-danger font-w600" : "text-muted"}`}>
                    {description.length}/264
                  </span>
                </div>
                <textarea
                  rows={1}
                  placeholder="Brief description of this group..."
                  value={description}
                  maxLength={264}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= 264) {
                      setDescription(val);
                      if (errors.description) {
                        setErrors((prev) => ({ ...prev, description: undefined }));
                      }
                    }
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                  }}
                  className={`clsFnLn w-100 form-control contacts-description-textarea ${errors.description ? "border-danger is-invalid" : ""
                    }`}
                  style={{ minHeight: "38px", maxHeight: "100px", overflowY: "auto", resize: "none" }}
                />
                {errors.description && (
                  <div className="text-danger font-12 mt-1">{errors.description}</div>
                )}
              </div>
            </div>

            {/* Sub-tabs: Domains & Contacts */}
            <ul className="nav contacts-group-modal-tabs">
              <li className="nav-item">
                <button type="button" className={`contacts-group-modal-tab-btn ${activeTab === "domains" ? "active" : ""}`} onClick={() => setActiveTab("domains")}>
                  {selectedLanguage?.LstLanguageCodes?.UI_Domains}
                  <span className="text-danger">*</span>
                </button>
              </li>
              <li className="nav-item">
                <button type="button" className={`contacts-group-modal-tab-btn ${activeTab === "contacts" ? "active" : ""}`} onClick={() => setActiveTab("contacts")}>
                  {selectedLanguage?.LstLanguageCodes?.UI_Contacts}
                </button>
              </li>
            </ul>

            {/* TAB 1: DOMAINS */}
            {activeTab === "domains" && (
              <div>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 pb-2">
                  <span className="text-dark font-w600 font-13">
                    {selectedLanguage?.LstLanguageCodes?.UI_SelectDomainsFromExistingOrAddCustom}
                  </span>
                  <div className="d-flex align-items-center gap-2 readerEdit-mail">
                    <input type="text" placeholder="e.g. example.com" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} onKeyDown={handleAddDomain} className="clsFnLn form-control contacts-domain-input" />
                    <button type="button" onClick={handleAddDomain} disabled={!domainInput.trim()} className="btn btn-primary border-r4 contacts-btn-compact">
                      {selectedLanguage?.LstLanguageCodes?.UI_AddDomain}
                    </button>
                    <button type="button" onClick={() => setIsImportDomainsModalOpen(true)} className="btn btn-outline-secondary border-r4 d-inline-flex align-items-center gap-1 contacts-btn-compact">
                      <span className="material-icons font-18">upload_file</span>
                      {selectedLanguage?.LstLanguageCodes?.UI_Import}
                    </button>
                  </div>
                </div>

                {/* 2-Panel Transfer Grid for Domains */}
                <div className="contacts-transfer-grid mb-3">
                  {/* Left Panel (In Use / Suggested Sub-Filter) */}
                  <div className="contacts-transfer-panel">
                    <div className="contacts-transfer-panel-header">
                      <div className="contacts-transfer-panel-pills">
                        <button type="button" className={`contacts-transfer-panel-pill-btn ${domainFilter === "inuse" ? "active" : ""}`} onClick={() => setDomainFilter("inuse")}>
                          {selectedLanguage?.LstLanguageCodes?.UI_InUse} ({inUseDomains.length})
                        </button>
                        <button type="button" className={`contacts-transfer-panel-pill-btn ${domainFilter === "suggested" ? "active" : ""}`} onClick={() => setDomainFilter("suggested")}>
                          {selectedLanguage?.LstLanguageCodes?.UI_Suggested} ({allAvailableDomains.length})
                        </button>
                      </div>
                    </div>
                    <div className="contacts-transfer-panel-search readerEdit-mail">
                      <span className="material-icons search-icon">search</span>
                      <input type="text" className="clsFnLn form-control" placeholder={domainFilter === "inuse" ? selectedLanguage?.LstLanguageCodes?.UI_SearchInUseDomains : selectedLanguage?.LstLanguageCodes?.UI_SearchSuggestedDomains}
                        value={searchQueryAvailableDomains} onChange={(e) => setSearchQueryAvailableDomains(e.target.value)}
                      />
                    </div>
                    <div className="contacts-transfer-panel-body">
                      {filteredLeftDomains.length === 0 ? (
                        <div className="p-4 text-center text-muted font-12">
                          {selectedLanguage?.LstLanguageCodes?.UI_NoDomainsFound}
                        </div>
                      ) : (
                        filteredLeftDomains.map((domain) => {
                          const isSelected = selectedDomains.includes(domain);
                          return (
                            <div key={domain} className={`contacts-transfer-item ${isSelected ? "selected" : ""}`} onClick={() => handleSelectDomainFromLeft(domain)}>
                              <span className="font-w600 text-dark font-13 text-truncate" title={`@${domain}`}>@{domain}</span>
                              <span className={`material-icons font-20 ${isSelected ? "text-success" : "text-primary"}`} title={isSelected ? selectedLanguage.LstLanguageCodes?.UI_Selected : selectedLanguage.LstLanguageCodes?.UI_AddToGroup}>
                                {isSelected ? "check_circle" : "add_circle_outline"}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Right Panel (Allowed Domains) */}
                  <div className="contacts-transfer-panel">
                    <div className="contacts-transfer-panel-header">
                      <span className="contacts-transfer-panel-title">
                        {selectedLanguage?.LstLanguageCodes?.UI_AllowedDomains}
                      </span>
                      <span className="contacts-transfer-panel-badge text-danger">
                        {selectedDomains.length}
                      </span>
                    </div>
                    <div className="contacts-transfer-panel-search readerEdit-mail">
                      <span className="material-icons search-icon">search</span>
                      <input type="text" className="clsFnLn form-control" placeholder={selectedLanguage?.LstLanguageCodes?.UI_SearchAllowedDomains}
                        value={searchQuerySelectedDomains} onChange={(e) => setSearchQuerySelectedDomains(e.target.value)} />
                    </div>
                    <div className="contacts-transfer-panel-body">
                      {filteredSelectedDomains.length === 0 ? (
                        <div className="p-4 text-center text-muted font-12">
                          {selectedLanguage?.LstLanguageCodes?.UI_NoDomainsSelected}
                        </div>
                      ) : (
                        filteredSelectedDomains.map((domain) => (
                          <div key={domain} className="contacts-transfer-item selected" onClick={() => handleRemoveDomainFromRight(domain)}>
                            <span className="font-w600 text-dark font-13 text-truncate" title={`@${domain}`}>@{domain}</span>
                            <span className="material-icons text-danger font-20" title="Remove">highlight_off</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Auto-Add Toggle Checkbox matching ContactsList.js & RDocs Theme */}
                {/* <div className="checkbox mt-3 mb-1 d-flex align-items-center">
                  <label className="ps-0 mb-0 d-flex align-items-center cursor-pointer">
                    <input type="checkbox" id="autoAddGroupSwitch" checked={autoAddUsers} onChange={(e) => setAutoAddUsers(e.target.checked)} />
                    <span className={`cr ${autoAddUsers ? "checked" : ""}`}>
                      <svg className="cr-icon-svg" viewBox="0 0 24 24" width="11" height="11" stroke="#ffffff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <i className="cr-icon material-icons">check</i>
                    </span>
                    <span className="font-13 text-dark font-w500 ms-2">
                      {selectedLanguage?.LstLanguageCodes?.UI_AutoAddAuthenticatedUsers || "Auto-add authenticated users from these domains to contacts and this group"}
                    </span>
                  </label>
                </div> */}
              </div>
            )}

            {/* TAB 2: CONTACTS */}
            {activeTab === "contacts" && (
              <div>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 pb-2">
                  <span className="text-dark font-w600 font-13">
                    {selectedLanguage?.LstLanguageCodes?.UI_SelectContactsOrAddImportNew}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <button type="button" onClick={() => setIsContactModalOpen(true)} className="btn btn-outline-secondary border-r4 d-inline-flex align-items-center gap-1 contacts-btn-compact">
                      <span className="material-icons font-18">add_circle</span> {selectedLanguage.LstLanguageCodes.UI_NewContact}
                    </button>
                    <button type="button" onClick={() => setIsImportModalOpen(true)} className="btn btn-outline-secondary border-r4 d-inline-flex align-items-center gap-1 contacts-btn-compact">
                      <span className="material-icons font-18">upload_file</span>
                      {selectedLanguage?.LstLanguageCodes?.UI_Import}
                    </button>
                  </div>
                </div>

                {/* 2-Panel Transfer Grid for Contacts */}
                <div className="contacts-transfer-grid mb-3">
                  {/* Left Panel (In Use / Suggested Sub-Filter) */}
                  <div className="contacts-transfer-panel">
                    <div className="contacts-transfer-panel-header">
                      <div className="contacts-transfer-panel-pills">
                        <button type="button" className={`contacts-transfer-panel-pill-btn ${contactFilter === "inuse" ? "active" : ""}`} onClick={() => setContactFilter("inuse")}>
                          {selectedLanguage?.LstLanguageCodes?.UI_InUse} ({inUseContacts.length})
                        </button>
                        <button type="button" className={`contacts-transfer-panel-pill-btn ${contactFilter === "suggested" ? "active" : ""}`} onClick={() => setContactFilter("suggested")}>
                          {selectedLanguage?.LstLanguageCodes?.UI_Suggested} ({filteredLeftContacts.length})
                        </button>
                      </div>
                    </div>
                    <div className="contacts-transfer-panel-search readerEdit-mail">
                      <span className="material-icons search-icon">search</span>
                      <input type="text" className="clsFnLn form-control" placeholder={contactFilter === "inuse" ? "Search in-use contacts..." : "Search suggested contacts..."}
                        value={searchQueryAvailable} onChange={(e) => setSearchQueryAvailable(e.target.value)} />
                    </div>
                    <div className="contacts-transfer-panel-body">
                      {filteredLeftContacts.length === 0 ? (
                        <div className="p-4 text-center text-muted font-12">
                          {selectedLanguage?.LstLanguageCodes?.UI_NoContactsFound}
                        </div>
                      ) : (
                        filteredLeftContacts.map((contact) => {
                          const contactEmail = (contact.email || "").toLowerCase().trim();
                          const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
                          const isSelected = selectedContactIds.map((id) => String(id).toLowerCase().trim()).includes(contactEmail);
                          const displayName = fullName || contact.email;
                          return (
                            <div key={contactEmail} className={`contacts-transfer-item ${isSelected ? "selected" : ""}`} onClick={() => handleSelectContactFromLeft(contact)}>
                              <div className="overflow-hidden">
                                <p className="font-w600 font-13 text-dark mb-0 text-truncate" title={displayName}>
                                  {displayName}
                                </p>
                                <p className="font-11 text-muted mb-0 text-truncate" title={contact.email}>{contact.email}</p>
                              </div>
                              <span className={`material-icons font-20 ms-2 ${isSelected ? "text-success" : "text-primary"}`} title={isSelected ? selectedLanguage?.LstLanguageCodes?.UI_Selected || "Selected" : selectedLanguage?.LstLanguageCodes?.UI_AddToGroup || "Add to Group"}>
                                {isSelected ? "check_circle" : "add_circle_outline"}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Right Panel (Group Members) */}
                  <div className="contacts-transfer-panel">
                    <div className="contacts-transfer-panel-header">
                      <span className="contacts-transfer-panel-title">
                        {selectedLanguage?.LstLanguageCodes?.UI_GroupMembers}
                      </span>
                      <span className="contacts-transfer-panel-badge text-danger">
                        {selectedContactsList.length}
                      </span>
                    </div>
                    <div className="contacts-transfer-panel-search readerEdit-mail">
                      <span className="material-icons search-icon">search</span>
                      <input type="text" className="clsFnLn form-control" placeholder="Search members..."
                        value={searchQuerySelected} onChange={(e) => setSearchQuerySelected(e.target.value)} />
                    </div>
                    <div className="contacts-transfer-panel-body">
                      {filteredSelectedContacts.length === 0 ? (
                        <div className="p-4 text-center text-muted font-12">
                          {selectedLanguage?.LstLanguageCodes?.UI_NoContactsSelected}
                        </div>
                      ) : (
                        filteredSelectedContacts.map((contact) => {
                          const contactEmail = (contact.email || "").toLowerCase().trim();
                          const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
                          const displayName = fullName || contact.email;
                          return (
                            <div key={contactEmail} className="contacts-transfer-item selected" onClick={() => handleRemoveContactFromRight(contact)}>
                              <div className="overflow-hidden">
                                <p className="font-w600 font-13 text-dark mb-0 text-truncate" title={displayName}>{displayName}</p>
                                <p className="font-11 text-muted mb-0 text-truncate" title={contact.email}>{contact.email}</p>
                              </div>
                              <span className="material-icons text-danger font-20 ms-2" title="Remove">highlight_off</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer (Fixed at Bottom) */}
          <div className="modal-footer d-flex align-items-center justify-content-end">
            <button type="button" title={selectedLanguage?.LstLanguageCodes?.UI_Cancel} className="btn btn-danger px-2 me-2 ms-1 w-140p truncate" onClick={onClose}>
              {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
            </button>
            <button type="button" title={initialGroup ? selectedLanguage?.LstLanguageCodes?.UI_Update : selectedLanguage?.LstLanguageCodes?.UI_Save}
              className="btn btn-primary px-2 ms-1 w-140p truncate" onClick={handleSubmit} disabled={!name.trim() || !aliasEmail.trim() || selectedDomains.length === 0}>
              {initialGroup ? selectedLanguage?.LstLanguageCodes?.UI_Update : selectedLanguage?.LstLanguageCodes?.UI_Save}
            </button>
          </div>
        </div>
      </div>

      {/* Inner Sub-Modal: New Contact */}
      {isContactModalOpen && (
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          initialContact={null}
          onSave={handleSaveContactFromModal}
          existingContacts={allCombinedContacts}
        />
      )}

      {/* Inner Sub-Modal: Import Contacts */}
      {isImportModalOpen && (
        <ImportContactsModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportContactsFromModal}
          existingContacts={allCombinedContacts}
          onOpenValidationPopup={(data) => {
            setValidationData(data);
            setIsValidationPopup(true);
          }}
          showAlert={(msg, type = "warning") => {
            setAlertMsg(msg);
            setAlertType(type);
          }}
        />
      )}

      {/* Confirmation Dialog: Save to Suggested Domains? */}
      {pendingDomainsToAdd && (
        <div className="modalCus contacts-modal-backdrop-elevated">
          <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-md">
            <div className="modal-content border-r4">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="mb-0 text-danger fw-bold font-20">
                  {selectedLanguage?.LstLanguageCodes?.UI_SaveToSuggestedDomains}
                </h4>
                <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0"
                  onClick={() => setPendingDomainsToAdd(null)}
                >
                  <span className="material-icons font-20">highlight_off</span>
                </button>
              </div>
              <div className="modal-body p-4 font-14">
                <p className="mb-2">
                  {selectedLanguage?.LstLanguageCodes?.UI_YouAdded} {pendingDomainsToAdd.length} {selectedLanguage.LstLanguageCodes.UI_NewContactsAlsoSaveToFutureGroups}
                </p>
                {/* <ul className="text-dark font-w600 mb-0 ps-3">
                  {pendingDomainsToAdd.map((d) => (<li key={d}>@{d}</li>))}
                </ul> */}
                <div className="contacts-confirmation-help-box">
                  <p className="mb-1">
                    <strong>{selectedLanguage.LstLanguageCodes.UI_NoGroupOnly}:</strong> {selectedLanguage.LstLanguageCodes.UI_NoGroupOnlyTip}
                  </p>
                  <p className="mb-0">
                    <strong>{selectedLanguage.LstLanguageCodes.UI_YesSaveToSuggested}:</strong> {selectedLanguage.LstLanguageCodes.UI_SaveToSuggestedTip}
                  </p>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-end align-items-center p-3 border-top">
                <button type="button" className="btn btn-danger px-3 me-2 ms-1 truncate"
                  onClick={() => {
                    setSelectedDomains((prev) => Array.from(new Set([...prev, ...pendingDomainsToAdd])));
                    setPendingDomainsToAdd(null);
                  }}
                >
                  {selectedLanguage?.LstLanguageCodes?.UI_NoGroupOnly}
                </button>
                <button type="button" className="btn btn-primary px-3 ms-1 truncate"
                  onClick={() => {
                    if (onDomainsImport) { onDomainsImport(pendingDomainsToAdd); }
                    setSelectedDomains((prev) => Array.from(new Set([...prev, ...pendingDomainsToAdd])));
                    setPendingDomainsToAdd(null);
                  }}
                >
                  {selectedLanguage?.LstLanguageCodes?.UI_YesSaveToSuggested}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog: Save to Contacts Directory? */}
      {pendingContactsToAdd && (
        <div className="modalCus contacts-modal-backdrop-elevated">
          <div className="modal-dialog modal-dialog-centered contacts-modal-dialog-md">
            <div className="modal-content border-r4">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0 text-danger fw-bold font-20">
                  {selectedLanguage?.LstLanguageCodes?.UI_SaveToContactsDirectory}
                </h5>
                <button type="button" className="btn btn-outline-danger Readerclose modalclose download" onClick={() => setPendingContactsToAdd(null)}>
                  <span className="material-icons font-18">highlight_off</span>
                </button>
              </div>
              <div className="modal-body p-4 font-14">
                <p className="font-14 text-dark mb-1">
                  {selectedLanguage.LstLanguageCodes.UI_YouAdded} <strong>{pendingContactsToAdd.length}</strong> {selectedLanguage.LstLanguageCodes.UI_NewContactsAdded}
                </p>
                <p className="font-13 text-secondary mb-0">
                  {selectedLanguage?.LstLanguageCodes?.UI_WouldYouLikeToSaveThemToGlobalContacts}
                </p>
                <div className="contacts-confirmation-help-box">
                  <p className="mb-1">
                    <strong>{selectedLanguage.LstLanguageCodes.UI_NoGroupOnly}:</strong> {selectedLanguage.LstLanguageCodes.UI_NoGroupOnlyContactTip}
                  </p>
                  <p className="mb-0">
                    <strong>{selectedLanguage.LstLanguageCodes.UI_YesSaveToSuggested}:</strong> {selectedLanguage.LstLanguageCodes.UI_SaveToSuggestedContactTip}
                  </p>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-end align-items-center p-3 border-top">
                <button type="button" className="btn btn-danger px-3 me-2 ms-1 truncate"
                  onClick={() => {
                    const hidden = pendingContactsToAdd.map((c) => ({ ...c, isHiddenFromDirectory: true }));
                    if (onContactsImport) { onContactsImport(hidden); }
                    setNewlyImportedContacts((prev) => [...prev, ...hidden]);
                    setSelectedContactIds((prev) => Array.from(new Set([...prev, ...hidden.map((c) => c.email.toLowerCase())])));
                    setPendingContactsToAdd(null);
                  }}
                >
                  {selectedLanguage?.LstLanguageCodes?.UI_NoGroupOnly || "No, Group Only"}
                </button>
                <button type="button" className="btn btn-primary px-3 ms-1 truncate"
                  onClick={() => {
                    if (onContactsImport) { onContactsImport(pendingContactsToAdd); }
                    setNewlyImportedContacts((prev) => [...prev, ...pendingContactsToAdd]);
                    setSelectedContactIds((prev) => Array.from(new Set([...prev, ...pendingContactsToAdd.map((c) => c.email.toLowerCase())])));
                    setPendingContactsToAdd(null);
                  }}
                >
                  {selectedLanguage?.LstLanguageCodes?.UI_SaveToDirectory}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Import Domains Modal */}
      {isImportDomainsModalOpen && (
        <ImportDomainsModal
          isOpen={isImportDomainsModalOpen}
          onClose={() => setIsImportDomainsModalOpen(false)}
          onImport={handleImportDomainsFromModal}
          existingDomains={selectedDomains}
          onOpenValidationPopup={(data) => {
            setValidationData(data);
            setIsValidationPopup(true);
          }}
          showAlert={(msg, type) => {
            setAlertMsg(msg);
            setAlertType(type);
          }}
        />
      )}
      {/* Validation Popup for File Errors */}
      {isValidationPopup && (
        <ValidationPopup
          isValidationPopup={isValidationPopup}
          handleCloseValidationModal={() => setIsValidationPopup(false)}
          selectedLanguage={selectedLanguage}
          duplicateInFile={validationData.duplicateInFile || []}
          emailLeanghtValidation={validationData.emailLeanghtValidation || []}
          invalidEmailsWithoutDotCom={validationData.invalidEmailsWithoutDotCom || []}
          localpartValiadation={validationData.localpartValiadation || []}
          longDomainEmails={validationData.longDomainEmails || []}
          longFirstName={validationData.longFirstName || []}
          longLastName={validationData.longLastName || []}
          invalidMobileNumbers={validationData.invalidMobileNumbers || []}
          unauthorizedCountryCodes={validationData.unauthorizedCountryCodes || []}
          duplicateMobileNumbers={validationData.duplicateMobileNumbers || []}
          missingMobileNumbers={validationData.missingMobileNumbers || []}
          missingCountryCodes={validationData.missingCountryCodes || []}
          longReference={validationData.longReference || []}
          longDescription={validationData.longDescription || []}
          invalidDomain={validationData.invalidDomain || []}
          missingRequiredFields={validationData.missingRequiredFields || []}
          inValidAuthType={[]}
          inValidMsgForBoth=""
          invalidCanPrintValues={[]}
          exceedMaxPrintsPerDoc={[]}
          maxPrintsWithoutCanPrint={[]}
          canPrintNotEnabledAtDocLevel={[]}
          maxPrintsExceedLimit={[]}
          invalidMaxPrintsValues={[]}
        />
      )}
    </div>
  );
}

export default ContactGroupModal;